import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

interface CommunitySwitcherContextValue {
	isOpen: boolean;
	toggle: () => void;
	close: () => void;
	panelRef: React.RefObject<HTMLElement | null>;
	triggerRef: React.RefObject<HTMLButtonElement | null>;
}

const CommunitySwitcherContext = createContext<CommunitySwitcherContextValue>({
	isOpen: false,
	toggle: () => {},
	close: () => {},
	panelRef: { current: null },
	triggerRef: { current: null },
});

export function CommunitySwitcherProvider({ children }: { children: React.ReactNode }) {
	const [isOpen, setIsOpen] = useState(false);
	const panelRef = useRef<HTMLElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const close = useCallback(() => setIsOpen(false), []);
	const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};

		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			if (panelRef.current?.contains(target)) return;
			if (triggerRef.current?.contains(target)) return;
			close();
		};

		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen, close]);

	return (
		<CommunitySwitcherContext.Provider value={{ isOpen, toggle, close, panelRef, triggerRef }}>
			{children}
		</CommunitySwitcherContext.Provider>
	);
}

export function useCommunitySwitcher() {
	return useContext(CommunitySwitcherContext);
}
